import React, { useState } from 'react';
import { ExplainerFocusPanelLayout } from '../../layouts';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { IOverviewWidgetProps } from './overview.models';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { HelpIcon, IconButton, SkillLevel, SkillPercentage, scorePercentageRAGColour, scoreLevelRAGColour, Panel } from '@keplerco/core';
import styles from './overview.module.css';
import classNames from 'classnames';
import { useAppState } from '../../../overmind';
import { SkillLevelsExplainerPanelLayout } from '../../layouts/focus-panel-layouts/skill-levels-explainer-panel/skill-levels-explainer-panel.layout';
import { DonutChart } from '../../../components/charts/donut/donut.chart';

export function OverviewWidget({ loading, title, donutChartData, firstNumberDisplayChartData, secondNumberDisplayChartData, thirdNumberDisplayChartData, isPercentageData }: IOverviewWidgetProps): JSX.Element {
  const { companyVariables } = useAppState();

  const [open, setOpen] = useState<{ title: string; description: string }>();

  const [explainerOpen, setExplainerOpen] = useState(false);

  if (loading) return <SkeletonLoader height="260px" />;

  return (
    <div className="card">
      {!donutChartData && !firstNumberDisplayChartData && !secondNumberDisplayChartData && !thirdNumberDisplayChartData ? (
        <EmptyState title="No overview found" />
      ) : (
        <React.Fragment>
          <React.Fragment>
            <h3>{title}</h3>
            <div className={styles.grid}>
              {!!donutChartData && (
                <div className={styles.donutGridItem}>
                  {!!donutChartData.description ? (
                    <React.Fragment>
                      <h4 style={{ marginBottom: 12 }}>
                        {donutChartData.label}
                        <IconButton iconType="fill" padding={5} onClick={donutChartData.label === 'Skill levels' ? () => setExplainerOpen(true) : () => setOpen({ title: donutChartData.label, description: donutChartData.description! })}>
                          <HelpIcon />
                        </IconButton>
                      </h4>

                      <DonutChart isPercentageData={isPercentageData} dataPoints={donutChartData.slices.map(slice => ({ name: slice.label, value: slice.value }))} fill={donutChartData.slices.map(slice => slice.colour)} label="Total courses" total={donutChartData.value} showLegend />
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <div className="heading5" style={{ marginBottom: 12 }}>
                        {donutChartData.label}
                      </div>

                      <DonutChart isPercentageData={isPercentageData} dataPoints={donutChartData.slices.map(slice => ({ name: slice.label, value: slice.value }))} fill={donutChartData.slices.map(slice => slice.colour)} label="Total courses" total={donutChartData.value} showLegend />
                    </React.Fragment>
                  )}
                </div>
              )}

              {!!firstNumberDisplayChartData && (
                <div className={styles.gridItem}>
                  {firstNumberDisplayChartData.chartType === 'skillLevel' && (
                    <div className={styles.skillLevelItem}>
                      <h4>
                        {firstNumberDisplayChartData.label}
                        <IconButton
                          iconType="fill"
                          padding={5}
                          onClick={() =>
                            setOpen({
                              title: 'Average skill score',
                              description: firstNumberDisplayChartData.description!,
                            })
                          }
                        >
                          <HelpIcon />
                        </IconButton>
                      </h4>

                      <SkillLevel
                        level={firstNumberDisplayChartData.value ?? companyVariables.minLevel}
                        minLevel={companyVariables.minLevel}
                        maxLevel={companyVariables.maxLevel}
                        noLevel={!firstNumberDisplayChartData.value}
                        dotColour={scoreLevelRAGColour(firstNumberDisplayChartData.value ?? companyVariables.minLevel, companyVariables.minLevel, companyVariables.maxLevel)}
                      />
                    </div>
                  )}

                  {firstNumberDisplayChartData.chartType === 'skillPercentage' && (
                    <div className={styles.skillLevelItem}>
                      <h4>
                        {firstNumberDisplayChartData.label}
                        <IconButton
                          iconType="fill"
                          padding={5}
                          onClick={() =>
                            setOpen({
                              title: 'Average skill score',
                              description: firstNumberDisplayChartData.description!,
                            })
                          }
                        >
                          <HelpIcon />
                        </IconButton>
                      </h4>

                      <SkillPercentage percentage={firstNumberDisplayChartData.value ?? 0} noPercentage={!firstNumberDisplayChartData.value} barColour={scorePercentageRAGColour(firstNumberDisplayChartData.value ?? 0)} />
                    </div>
                  )}

                  {firstNumberDisplayChartData.chartType === 'numberDisplay' && (
                    <React.Fragment>
                      {!!firstNumberDisplayChartData.description ? (
                        <React.Fragment>
                          <h4>
                            {firstNumberDisplayChartData.label}

                            <IconButton iconType="fill" onClick={() => setOpen({ title: firstNumberDisplayChartData.label, description: firstNumberDisplayChartData.description! })}>
                              <HelpIcon />
                            </IconButton>
                          </h4>

                          <span className="h2">
                            {firstNumberDisplayChartData.value ?? 0}
                            {firstNumberDisplayChartData.valueType === 'percentage' && '%'}
                          </span>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <h4>{firstNumberDisplayChartData.label}</h4>

                          <span className="h2">
                            {firstNumberDisplayChartData.value ?? 0}
                            {firstNumberDisplayChartData.valueType === 'percentage' && '%'}
                          </span>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}
                </div>
              )}

              {!!secondNumberDisplayChartData && (
                <div className={classNames([styles.gridItem], [styles.secondGridItem])}>
                  {!!secondNumberDisplayChartData.description ? (
                    <React.Fragment>
                      <h4>
                        {secondNumberDisplayChartData.label}

                        <IconButton iconType="fill" onClick={() => setOpen({ title: secondNumberDisplayChartData.label, description: secondNumberDisplayChartData.description! })}>
                          <HelpIcon />
                        </IconButton>
                      </h4>
                      <span className="h2">
                        {secondNumberDisplayChartData.value ?? 0}
                        {secondNumberDisplayChartData.valueType === 'percentage' && '%'}
                      </span>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <h4>{secondNumberDisplayChartData.label}</h4>

                      <span className="h2">
                        {secondNumberDisplayChartData.value ?? 0}
                        {secondNumberDisplayChartData.valueType === 'percentage' && '%'}
                      </span>
                    </React.Fragment>
                  )}
                </div>
              )}

              {!!thirdNumberDisplayChartData && (
                <div className={classNames([styles.gridItem], [styles.thirdGridItem])}>
                  {!!thirdNumberDisplayChartData.description ? (
                    <React.Fragment>
                      <h4>
                        {thirdNumberDisplayChartData.label}

                        <IconButton iconType="fill" onClick={() => setOpen({ title: thirdNumberDisplayChartData.label, description: thirdNumberDisplayChartData.description! })}>
                          <HelpIcon />
                        </IconButton>
                      </h4>

                      <span className="h2">
                        {thirdNumberDisplayChartData.value ?? 0}
                        {thirdNumberDisplayChartData.valueType === 'percentage' && '%'}
                      </span>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <h4>{thirdNumberDisplayChartData.label}</h4>

                      <span className="h2">
                        {thirdNumberDisplayChartData.value ?? 0}
                        {thirdNumberDisplayChartData.valueType === 'percentage' && '%'}
                      </span>
                    </React.Fragment>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>

          <Panel open={!!open} onClose={() => setOpen(undefined)}>
            {!!open && (
              <ExplainerFocusPanelLayout title={open.title} onClose={() => setOpen(undefined)}>
                {open.description}
              </ExplainerFocusPanelLayout>
            )}
          </Panel>

          {companyVariables.useLevels && (
            <Panel open={explainerOpen} onClose={() => setExplainerOpen(false)}>
              <SkillLevelsExplainerPanelLayout onClose={() => setExplainerOpen(false)} />
            </Panel>
          )}
        </React.Fragment>
      )}
    </div>
  );
}
